import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ApiError, get, onAuthFailure, post, setCsrfToken } from "./api.ts";
import { hasUnsavedChanges } from "./guard.tsx";
import { ReloginDialog } from "../components/relogin.tsx";
import type { SessionDto, UserDto } from "../../shared/auth.ts";

interface SessionState {
  user: UserDto | null;
  loading: boolean;
  /** After login/accept/password change: adopt the new session and drop every cached protected query. */
  adopt(s: SessionDto): void;
  logout(redirectTo?: string): Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);
export const ME_KEY = ["session", "me"] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: async () => {
      // 200 {data: null} when anonymous: public pages don't produce a 401 in the console (UX-23).
      const res = await get<{ data: SessionDto | null }>("/auth/session");
      if (!res.data) return null;
      setCsrfToken(res.data.csrf_token);
      return res.data.user;
    },
    staleTime: 5 * 60_000,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
  });

  // E-mail of the session that just expired; non-null while the re-login dialog is open.
  const [relogin, setRelogin] = useState<string | null>(null);
  const lastEmail = useRef<string | null>(null);
  useEffect(() => {
    if (me.data) lastEmail.current = me.data.email;
  }, [me.data]);

  useEffect(
    () =>
      onAuthFailure((err) => {
        if (err.code !== "UNAUTHENTICATED") return;
        // With unsaved edits on screen, sign in again over the page instead of discarding the work (UX-19).
        if (hasUnsavedChanges() && lastEmail.current) {
          setRelogin(lastEmail.current);
          return;
        }
        // Unmount protected data: nothing from the old session may stay in memory (docs/06 async and stale data).
        qc.clear();
        qc.setQueryData(ME_KEY, null);
        navigate("/login");
      }),
    [qc, navigate],
  );

  const value: SessionState = {
    user: me.data ?? null,
    loading: me.isPending,
    adopt(s) {
      setCsrfToken(s.csrf_token);
      qc.clear();
      qc.setQueryData(ME_KEY, s.user);
    },
    async logout(redirectTo = "/login") {
      try {
        await post("/auth/logout");
      } finally {
        setCsrfToken("");
        qc.clear();
        qc.setQueryData(ME_KEY, null);
        navigate(redirectTo);
      }
    },
  };
  return (
    <Ctx.Provider value={value}>
      {children}
      {relogin && (
        <ReloginDialog
          email={relogin}
          onDone={(s) => {
            const same = s.user.email === relogin;
            setRelogin(null);
            if (same) {
              setCsrfToken(s.csrf_token);
              qc.setQueryData(ME_KEY, s.user);
            } else value.adopt(s);
          }}
          onGiveUp={() => {
            setRelogin(null);
            qc.clear();
            qc.setQueryData(ME_KEY, null);
            navigate("/login");
          }}
        />
      )}
    </Ctx.Provider>
  );
}

export function useSession() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

/** Only same-origin app paths survive a login redirect; no open redirects. */
export function safeNext(raw: string | null): string | null {
  if (!raw) return null;
  return /^\/(planos|convite|perfil)(\/[A-Za-z0-9_-]+)*$/.test(raw) ? raw : null;
}
